export interface SessionAttributionInput {
	utmSource?: string | null;
	utmMedium?: string | null;
	utmCampaign?: string | null;
	referrer?: string | null;
}

/** Shown in `firstAttributedSource` / `lastAttributedSource` when a UTM field is absent. */
export const ATTRIBUTION_NOT_SET_LABEL = "(not set)";

function trimOrEmpty(s: string | null | undefined): string {
	return typeof s === "string" ? s.trim() : "";
}

function hasAnyUtm(input: SessionAttributionInput): boolean {
	return (
		trimOrEmpty(input.utmSource).length > 0 ||
		trimOrEmpty(input.utmMedium).length > 0 ||
		trimOrEmpty(input.utmCampaign).length > 0
	);
}

/**
 * Single marketing-attribution string for `IdentityPerson.firstAttributedSource` /
 * `lastAttributedSource` (and PostHog person traits).
 *
 * Format: `{medium} | {source} | {campaign}`
 *
 * - When any UTM is present: uses `utm_medium`, `utm_source`, `utm_campaign` respectively;
 *   each missing slot is {@link ATTRIBUTION_NOT_SET_LABEL}.
 * - When no UTMs but `referrer` is a valid http(s) URL: `referral | {hostname} | {pathname}`,
 *   with `www.` stripped from the hostname; empty pathname is normalized to `/`.
 * - Otherwise (true direct / no signal): `(not set) | direct | (not set)`.
 */
export function resolveAttributedSource(
	input: SessionAttributionInput,
): string {
	if (hasAnyUtm(input)) {
		const medium =
			trimOrEmpty(input.utmMedium) || ATTRIBUTION_NOT_SET_LABEL;
		const source =
			trimOrEmpty(input.utmSource) || ATTRIBUTION_NOT_SET_LABEL;
		const campaign =
			trimOrEmpty(input.utmCampaign) || ATTRIBUTION_NOT_SET_LABEL;
		return `${medium} | ${source} | ${campaign}`;
	}

	const ref = trimOrEmpty(input.referrer);
	if (ref.length > 0) {
		try {
			const url = new URL(ref);
			if (url.protocol === "http:" || url.protocol === "https:") {
				const hostRaw = url.hostname.replace(/^www\./i, "");
				const host =
					hostRaw.length > 0 ? hostRaw : ATTRIBUTION_NOT_SET_LABEL;
				const pathname = url.pathname.length > 0 ? url.pathname : "/";
				return `referral | ${host} | ${pathname}`;
			}
		} catch {
			// invalid referrer → fall through to direct
		}
	}

	return `${ATTRIBUTION_NOT_SET_LABEL} | direct | ${ATTRIBUTION_NOT_SET_LABEL}`;
}
